

import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import type { HTMLAttributes, MouseEvent as ReactMouseEvent, TdHTMLAttributes, ThHTMLAttributes } from "react";
import { cn } from "@/lib/utils"

const Table = forwardRef<
  HTMLTableElement,
  HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <table
    ref={ref}
    className={cn("caption-bottom text-body", className)}
    {...props}
  />
))
Table.displayName = "Table"

const TableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("border-none", className)} {...props} />
))
TableHeader.displayName = "TableHeader"

const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("", className)}
    {...props}
  />
))
TableBody.displayName = "TableBody"

const TableFooter = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "bg-primary/5 dark:bg-primary/10 font-medium border-t",
      className
    )}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

const TableRow = forwardRef<
  HTMLTableRowElement,
  HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "relative border-b border-border/20 min-h-[3rem]",
      className
    )}
    {...props}
  />
))
TableRow.displayName = "TableRow"

interface TableHeadProps extends Omit<ThHTMLAttributes<HTMLTableCellElement>, 'onResize'> {
  isResizable?: boolean;
  onResize?: (width: number) => void;
}

const TableHead = forwardRef<
  HTMLTableCellElement,
  TableHeadProps
>(({ className, isResizable, onResize, style, ...props }, ref) => {
  const [isResizing, setIsResizing] = useState(false);
  const thRef = useRef<HTMLTableCellElement | null>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);
  const resizerRef = useRef<HTMLDivElement | null>(null);

  useImperativeHandle(ref, () => thRef.current as HTMLTableCellElement);
  
  const handleResizerMouseDown = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    if (!thRef.current || !isResizable) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const columnId = thRef.current.getAttribute('data-column');
    if (columnId === 'view') return;
    
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = thRef.current.offsetWidth;
    
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!thRef.current) return;
      
      if (columnId === 'view') return;
      
      const widthChange = e.clientX - startXRef.current;
      const newWidth = startWidthRef.current + widthChange;
      const minWidth = 40;
      const finalWidth = Math.max(newWidth, minWidth);
      
      if (onResize) onResize(finalWidth);
      
      if (resizerRef.current) {
        resizerRef.current.classList.add('active');
      }
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
      
      if (resizerRef.current) {
        resizerRef.current.classList.remove('active');
      }
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [isResizable, onResize]);

  return (
    <th
      ref={thRef}
      className={cn(
        "h-14 text-left align-middle font-semibold text-foreground select-none px-3 py-4 relative text-xs uppercase tracking-wider border-r border-border/15 last:border-r-0 bg-background",
        isResizing && "opacity-70",
        className
      )}
      style={style}
      {...props}
    >
      {props.children}
      
      {isResizable && (
        <div 
          ref={resizerRef}
          className="column-resizer"
          onMouseDown={handleResizerMouseDown}
          onClick={(e) => e.stopPropagation()}
          aria-hidden="true"
          title="Drag to resize column"
        />
      )}
    </th>
  )
})
TableHead.displayName = "TableHead"

const TableCell = forwardRef<
  HTMLTableCellElement,
  TdHTMLAttributes<HTMLTableCellElement>
>(({ className, style, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "px-3 py-4 align-middle text-foreground overflow-hidden text-ellipsis border-r border-border/15 last:border-r-0",
      className
    )}
    style={style}
    {...props}
  />
))
TableCell.displayName = "TableCell"

const TableCaption = forwardRef<
  HTMLTableCaptionElement,
  HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-caption", className)}
    {...props}
  />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
